import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

/* -------------------------------------------------------------------------- */
/*  Shared modal logic                                                        */
/*  Used by BOTH the delegated `.mrk-quick-add-btn` button (collection cards and  */
/*  the Boost app) and the <quick-add-component> element (recommendations &   */
/*  upsells). Single source of truth - no duplicated fetch/render code.       */
/* -------------------------------------------------------------------------- */

/**
 * The curated quick-add template (templates/product.quick-view.json). It has
 * `layout: false` and only the product-information section, so ?view=quick-view
 * returns just the buy-box (no header/footer/other sections) and stays editable
 * in the theme customizer.
 */
const QUICK_ADD_VIEW = 'quick-view';

/**
 * Fetches the buy-box: the lean quick-view template first, falling back to the
 * full product page. (The buy-box is extracted from it in injectBuyBox.)
 * @param {string} productPageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document | null>}
 */
async function fetchBuyBox(productPageUrl, signal) {
  if (!productPageUrl) return null;

  const quickView = await fetchQuickView(productPageUrl, signal);
  if (quickView) {
    window.mrkQuickAddView = QUICK_ADD_VIEW;
    return quickView;
  }

  // Fallback: full product page (e.g. if the quick-view template isn't present).
  window.mrkQuickAddView = '';
  const response = await fetch(productPageUrl, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
  }
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/**
 * Fetches ?view=quick-view and returns it only if it rendered the buy-box.
 * @param {string} productPageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document | null>}
 */
async function fetchQuickView(productPageUrl, signal) {
  try {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('view', QUICK_ADD_VIEW);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('[data-product-grid-content]') ? doc : null;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    return null;
  }
}

/**
 * Injects the fetched buy-box into the modal and boots the framing engine.
 * @param {Document} newHtml
 * @param {string} productPageUrl
 */
function injectBuyBox(newHtml, productPageUrl) {
  const productGrid = newHtml.querySelector('[data-product-grid-content]');
  const modalContent = document.getElementById('quick-add-modal-content');

  if (!productGrid || !modalContent) return;

  // Framing products (the custom mrk buy-box) carry a variant-data script;
  // simple products (single default variant, no variant picker) don't. We inject
  // the whole .product-details for both and let CSS lay it out.
  const isFramingProduct = productGrid.querySelector('#mrk-main-variant-data') != null;

  morph(modalContent, productGrid);

  // Point the modal's "See full details" link at this product's PDP.
  const seeDetailsLink = document.querySelector('#quick-add-dialog .drool-quick-see-details');
  if (seeDetailsLink instanceof HTMLAnchorElement && productPageUrl) seeDetailsLink.href = productPageUrl;

  if (isFramingProduct) {
    // Capture the real product main section id (template--123__main) from the
    // fetched page's wrapper so mrk.js can refresh the frame picker on size
    // change with a working URL.
    const sectionWrapper = productGrid.closest('.shopify-section');
    window.mrkQuickAddSectionId = sectionWrapper?.id?.replace('shopify-section-', '') || '';

    // The host page may carry its OWN mrk buy-box - any product page, whether the
    // modal was opened from the bundle builder (pack mode) or from a
    // recommendation/upsell card. mrk.js drives the buy-box through page-global
    // selectors ($('#mrk-main-variant-data'), '.mrk-frame-switcher', ...) which
    // resolve to the FIRST match in the DOM - i.e. the host product, not the
    // modal. That made the modal resolve the HOST's framing mode (e.g. the
    // framing service never appeared for a child-frame product quick-added on a
    // product page). So on EVERY open: snapshot the host globals before the
    // injected inline scripts overwrite them, and lift the host's colliding
    // buy-box elements out of the DOM for the modal's lifetime (both restored
    // by the dialog-close handler).
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog) {
      const hostHasBuyBox = Array.from(document.querySelectorAll(BB_MAIN_BUYBOX_SELECTOR)).some(
        (el) => !dialog.contains(el)
      );
      if (hostHasBuyBox) {
        bbSaveHostGlobals();
        bbDetachMain(dialog);
      }
    }

    // <script> nodes injected via morph are inert; re-execute the inline data
    // scripts (window.main_product_data, framing globals, #mrk-main-variant-data)
    // so they reflect THIS product, then run the shared mrk bootstrap.
    activateScripts(modalContent);
    if (typeof window.mrkInitQuickAdd === 'function') window.mrkInitQuickAdd();
  }
}

/**
 * Re-executes inline executable <script>s inside an injected container. Scripts
 * inserted via morph/DOMParser are flagged non-executing by the HTML spec, so we
 * clone each into a fresh element to run it. JSON/data scripts (e.g.
 * #mrk-main-variant-data) are read via textContent and must NOT run.
 * @param {HTMLElement} container
 */
function activateScripts(container) {
  for (const oldScript of container.querySelectorAll('script')) {
    const type = (oldScript.getAttribute('type') || '').toLowerCase();
    const isExecutable =
      type === '' || type === 'text/javascript' || type === 'application/javascript' || type === 'module';
    if (!isExecutable) continue;

    const newScript = document.createElement('script');
    for (const attr of Array.from(oldScript.attributes)) newScript.setAttribute(attr.name, attr.value);
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  }
}

/* -------------------------------------------------------------------------- */
/*  Bundle-builder "pack mode" buy-box scoping                                 */
/*  When the quick view is opened from the bundle builder it runs on a PRODUCT */
/*  page that already has its own buy-box. The mrk buy-box JS uses page-global */
/*  selectors + globals (built for one product per page), so injecting a       */
/*  second one collides. For pack mode we (a) snapshot the host page's product */
/*  globals so the inject doesn't corrupt the main product, and (b) run init   */
/*  with the host's colliding buy-box elements briefly lifted out so the        */
/*  global selectors resolve to the MODAL's elements. (Full interactive        */
/*  scoping of every change handler is a later step.)                          */
/* -------------------------------------------------------------------------- */

/** Product globals the injected buy-box inline scripts overwrite (see snippets). */
const BB_HOST_GLOBAL_KEYS = [
  'main_product_data',
  'free_framing_service',
  'show_default_black_variant',
  'FrameType',
  'availableFrameIDs',
  'child_frame_sizes',
  'framed_print_only_sizes',
  'framed_print_only_variant_ids',
  'child_frame_variant_ids',
  'mrk_unframed_value',
];
/** @type {Record<string, any> | null} */
let bbHostGlobals = null;

/** Snapshot host-page product globals BEFORE the injected scripts overwrite them. */
function bbSaveHostGlobals() {
  bbHostGlobals = {};
  for (const key of BB_HOST_GLOBAL_KEYS) bbHostGlobals[key] = window[key];
}

/** Restore host-page product globals (called when the pack quick view closes). */
function bbRestoreHostGlobals() {
  if (!bbHostGlobals) return;
  for (const key of BB_HOST_GLOBAL_KEYS) window[key] = bbHostGlobals[key];
  bbHostGlobals = null;
}

/**
 * The host PDP's native buy-box elements that mrk.js's page-global selectors target.
 * While the pack quick view is open these are lifted OUT of the DOM so every mrk.js
 * operation (init AND every change-event cascade) resolves to the MODAL's copies —
 * otherwise the main product (first in the DOM) wins and its logic removes/rebuilds
 * the modal's pickers a few ms after they render.
 */
const BB_MAIN_BUYBOX_SELECTOR =
  'variant-picker, variant-picker-cards, frame-variant-picker, #mrk-main-variant-data, .drool-picker-stock, .mrk-product-form-wrapper, .mrk-hidden-inputs, .mrk-framing-service-wrapper, .free-framing-service';

/** @type {{ el: Element, placeholder: Comment }[] | null} */
let bbMainStash = null;

/**
 * Lift the host PDP's native buy-box elements out of the DOM (placeholder swap) for
 * the WHOLE time the pack quick view is open. No-op if already lifted.
 * @param {HTMLElement} dialog
 */
function bbDetachMain(dialog) {
  if (bbMainStash) return; // already lifted (e.g. re-open before close)
  bbMainStash = [];
  document.querySelectorAll(BB_MAIN_BUYBOX_SELECTOR).forEach((el) => {
    if (dialog.contains(el)) return; // keep the modal's own copies
    const placeholder = document.createComment('bb-qv-main');
    if (el.parentNode) {
      el.parentNode.replaceChild(placeholder, el);
      bbMainStash.push({ el, placeholder });
    }
  });
}

/** Put the host PDP's native buy-box elements back exactly where they were. */
function bbReattachMain() {
  if (!bbMainStash) return;
  for (const { el, placeholder } of bbMainStash) {
    if (placeholder.parentNode) placeholder.parentNode.replaceChild(el, placeholder);
  }
  bbMainStash = null;
}

/** Shows/hides the modal's loading overlay. @param {boolean} show */
function toggleLoader(show) {
  document.querySelector('#quick-add-dialog [data-quick-add-loader]')?.toggleAttribute('hidden', !show);
}

/** @returns {QuickAddDialog | null} */
function getDialog() {
  const dialog = document.getElementById('quick-add-dialog');
  return dialog instanceof QuickAddDialog ? dialog : null;
}

/* -------------------------------------------------------------------------- */
/*  Delegated `.mrk-quick-add-btn` trigger                                        */
/*  A plain button carrying data-product-url. Works in the theme cards AND in */
/*  the Boost app (no web component / Horizon `on:` binding needed).          */
/* -------------------------------------------------------------------------- */

/** @type {Map<string, Document>} Parsed buy-box docs cached by product URL. */
const buyBoxCache = new Map();
/** @type {AbortController | null} */
let buttonAbortController = null;

/**
 * Opens the quick-add modal for a product URL: shows the modal immediately with
 * a loading overlay, fetches + renders in the background, then reveals it.
 * @param {string} productPageUrl
 */
async function openQuickAdd(productPageUrl) {
  if (!productPageUrl) return;
  const dialog = getDialog();
  if (!dialog) return;

  // Show the loader BEFORE opening the dialog: on mobile the dialog only gets
  // its height from the "loader visible" CSS rule, so opening first would paint
  // a zero-height (invisible) box on the first, uncached open.
  let doc = buyBoxCache.get(productPageUrl);
  const needsFetch = !doc;
  if (needsFetch) toggleLoader(true);

  dialog.showDialog();

  try {
    if (!doc) {
      buttonAbortController?.abort();
      buttonAbortController = new AbortController();
      try {
        doc = await fetchBuyBox(productPageUrl, buttonAbortController.signal);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        throw error;
      } finally {
        buttonAbortController = null;
      }
      if (doc) buyBoxCache.set(productPageUrl, doc);
    }

    if (doc) {
      const fresh = new DOMParser().parseFromString(doc.documentElement.outerHTML, 'text/html');
      injectBuyBox(fresh, productPageUrl);
    }
  } finally {
    toggleLoader(false);
  }
}

/**
 * Opens the quick-add modal from OUTSIDE this module (the bundle builder). When
 * `pack` is set, the dialog is flagged `data-bb-pack`, which (via CSS in
 * quick-add-modal.liquid) hides the native Add-to-cart / framing service and reveals
 * the quick-view template's "Add to pack" CTA instead. Every normal open path clears
 * the flag first (below) so it never leaks into a collection-page quick add.
 * @param {string} productPageUrl
 * @param {{ pack?: boolean }} [options]
 */
window.mrkOpenQuickAdd = function (productPageUrl, options) {
  getDialog()?.toggleAttribute('data-bb-pack', !!(options && options.pack));
  return openQuickAdd(productPageUrl);
};

// Delegated on `document` so it works for buttons added at any time (incl. the
// Boost app rendering cards after page load - no per-button binding needed).
// Capture phase + stopPropagation so the button wins over the surrounding card
// link/handlers (e.g. Boost's product link) and opens the modal instead of
// navigating to the PDP.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.mrk-quick-add-btn');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    getDialog()?.toggleAttribute('data-bb-pack', false); // never inherit the bundle "pack mode"
    openQuickAdd(button.getAttribute('data-product-url') || '');
  },
  true
);

// The modal layout differs between mobile and desktop, so close it when the
// breakpoint changes (matches the previous quick-add-component behaviour).
mediaQueryLarge.addEventListener('change', () => getDialog()?.closeDialog());

/* -------------------------------------------------------------------------- */
/*  <quick-add-component> (recommendations & upsells)                         */
/*  Unchanged trigger behaviour; reuses the shared modal logic above.         */
/* -------------------------------------------------------------------------- */

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialog = getDialog();
    if (!dialog) return;
    dialog.toggleAttribute('data-bb-pack', false); // recommendations/upsells are never pack mode

    this.toggleAttribute('stay-visible', true);
    dialog.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });

    // Show the loader before opening so the dialog has height on first paint
    // (see openQuickAdd - mobile height comes from the loader-visible rule).
    const needsFetch = !this.#cachedProductHtml;
    if (needsFetch) toggleLoader(true);

    dialog.showDialog();

    try {
      if (needsFetch) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        try {
          this.#cachedProductHtml = await fetchBuyBox(this.productPageUrl, this.#abortController.signal);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;
          throw error;
        } finally {
          this.#abortController = null;
        }
      }

      if (this.#cachedProductHtml) {
        const fresh = new DOMParser().parseFromString(this.#cachedProductHtml.documentElement.outerHTML, 'text/html');
        injectBuyBox(fresh, this.productPageUrl);
      }
    } finally {
      toggleLoader(false);
    }
  };

  #closeQuickAddModal = () => {
    getDialog()?.closeDialog();
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    // Pack mode (bundle builder): put the host PDP's buy-box back in the DOM, restore
    // its globals, and drop the pack flag, so the main product is unaffected afterwards.
    bbReattachMain();
    bbRestoreHostGlobals();
    this.removeAttribute('data-bb-pack');

    // Drop the injected buy-box now that the dialog is hidden (DialogCloseEvent fires
    // after dialog.close()). Otherwise its #mrk-main-variant-data duplicates the host's
    // restored copy and its stale pickers/inputs keep matching mrk.js's unscoped
    // selectors, corrupting host reads until the next open replaces the content.
    document.getElementById('quick-add-modal-content')?.replaceChildren();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
